import { DiscoveredAccount } from '../../utils';
import { FieldErrors } from '../../types';

export type LoginData = {
  captcha?: string | null;
  email: string;
  password?: string;
};

export type LoginFieldFlags = Record<keyof LoginData, boolean>;

export type LoginStatus = 'initial' | 'submitting' | 'success' | 'error';

export type LoginResult = {
  errors?: FieldErrors[];
};

export type LoginErrors = {
  captcha?: string;
  email?: string;
  error?: string;
  password?: string;
};

/**
 * Why the browser still needs confirming. The login's 403 `reason` is
 * new_device or unavailable; the code step's 403 `code` is any of the others
 * (or unavailable):
 * - new_device: this browser isn't trusted for the account; a code was emailed
 * - code_invalid: the code was wrong or malformed, or from before the latest
 *   resend
 * - code_locked: too many wrong guesses on this code; a new code is needed
 * - code_expired: there was no live code, so a new one was emailed
 * - code_limit: the user's daily or 30-day wrong-guess limit was reached
 * - send_capped: the user's daily limit of code emails was reached
 * - unavailable: a code couldn't be sent or checked right now
 *
 * What each one means for the code step is in `newDeviceReasons.ts`.
 */
export const NEW_DEVICE_CONFIRMATION_REASONS = [
  'new_device',
  'code_invalid',
  'code_locked',
  'code_expired',
  'code_limit',
  'send_capped',
  'unavailable',
] as const;

/**
 * A reason from the backend, or `unknown` for one this version doesn't know
 * (the backend's fallback message is shown for it)
 */
export type NewDeviceConfirmationReason =
  | (typeof NEW_DEVICE_CONFIRMATION_REASONS)[number]
  | 'unknown';

/**
 * A new_device_confirmation_required challenge: the password was correct, but
 * no session was created until this browser is confirmed with an emailed code.
 */
export type NewDeviceChallenge = {
  reason: NewDeviceConfirmationReason;
  // Fallback text from the backend, only shown for an unknown reason
  message?: string;
};

/**
 * What the last request from the sign-in code step did: submit a code, or ask
 * for a new one
 */
export type NewDeviceCodeAction = 'submit' | 'resend';

/**
 * A response from the sign-in code step's endpoints. They use the standard
 * API error format, so the reason is in `errors[].code`.
 */
export type CodeStepResponseBody = {
  errors?: {
    code?: string;
    detail?: string;
    source?: { pointer?: string };
    status?: number;
  }[];
};

/**
 * What a code-step response means for the step:
 * - confirmed: the code was right and the response signed the user in
 * - challenge: the browser still needs confirming, for this reason
 * - expired: there's no pending sign-in, so the password step takes over
 * - redirect: a step the password step would redirect to (2FA)
 * - error: the step stays as it is, with this message
 */
export type CodeStepResult =
  | { type: 'confirmed' }
  | { type: 'challenge'; challenge: NewDeviceChallenge }
  | { type: 'expired'; message: string }
  | { type: 'redirect'; url: string }
  | { type: 'error'; message: string };

export type LoginOutput = {
  account?: DiscoveredAccount | undefined;
  errors?: LoginErrors;
  didRedirect: boolean;
  newDeviceChallenge?: NewDeviceChallenge;
  type?: LoginResultType;
};

export type UseLoginInput = {
  /**
   * Force email to be a certain value
   */
  email?: string;
  /**
   * Default email value, which can be edited
   */
  defaultEmail?: string;
  /**
   * Ignore auth_type cookie and force LoginForm to display
   */
  forceLoginDisplayForm?: boolean;
  /**
   * Url of page to redirect user to after successfully logging in
   */
  next?: string | undefined;
  /**
   * Routing callback to integrate with router. Will use window.href.location directly when not specified
   */
  routeTo?: (url: string) => void;

  /**
   * Should we render Google SSO?
   */
  googleSSOEnabled?: boolean;

  /**
   * Should we render Facebook SSO?
   */
  facebookSSOEnabled?: boolean;

  /**
   * Should we render Microsoft SSO?
   */
  microsoftSSOEnabled?: boolean;

  /**
   * Should we render Continue with SSO?
   */
  SAMLSSOEnabled?: boolean;
};

export enum LoginResultType {
  IS_2FA_REQUIRED = 'IS_2FA_REQUIRED',
  IS_BAD_CREDENTIALS = 'IS_BAD_CREDENTIALS',
  IS_CAPTCHA_REQUIRED = 'IS_CAPTCHA_REQUIRED',
  IS_EMAIL_VERIFICATION_REQUIRED = 'IS_EMAIL_VERIFICATION_REQUIRED',
  IS_LOCKED = 'IS_LOCKED',
  IS_MISSING_CSRF = 'IS_MISSING_CSRF',
  IS_NEW_DEVICE_CONFIRMATION_REQUIRED = 'IS_NEW_DEVICE_CONFIRMATION_REQUIRED',
  IS_PENDING_WORKSPACE_ACCESS = 'IS_PENDING_WORKSPACE_ACCESS',
  IS_VALID_LOGIN = 'IS_VALID_LOGIN',
  TRYING_TOO_OFTEN = 'TRYING_TOO_OFTEN',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}
